import { Close } from '@mui/icons-material';
import {
	Box,
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Stack,
	Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import SidePanelDialog from 'src/globalComponents/SidePanelDialog';
import AccessoryDataCard from './AccessoryDataCard';
import { useApi } from 'src/hooks/useApi';
import Accessory, { AccessoryMaterial } from 'src/types/shared/accessory';
import AccessoryMaterialDataCard from './AccessoryMaterialDataCard';
import AddIcon from '@mui/icons-material/Add';
import verifyAccessory from 'src/utils/verifyAccessory';
import { toastSuccess } from 'src/Toaster/Toast';
import Spinner from 'src/globalComponents/spinner';
import SpinnerSize from 'src/enums/spinnerSize';

const defaultMaterial: AccessoryMaterial = {
	name: '',
	unitsPerAccessory: 1,
	maxAccessories: 1,
	price: 0,
	category: 'Material',
};

const defaultLabor: AccessoryMaterial = {
	name: 'Labor',
	unitsPerAccessory: 1,
	maxAccessories: 1,
	price: 0,
	category: 'Labor',
};

const EditAccessory: React.FC<{
	accessory: Accessory;
	isOpen: boolean;
	onClose: () => void;
	onRefresh: () => void;
}> = ({ accessory, isOpen, onClose, onRefresh }) => {
	const api = useApi();
	const [calculationTypes, setCalculationTypes] = useState([]);
	const [accessoryData, setAccessoryData] = useState<Accessory>(accessory);
	const [error, setError] = useState<string | null>(null);
	const [apiError, setApiError] = useState<string | null>(null);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		(async () => {
			setCalculationTypes(await api.accessory.getAccessoryCalculationTypes());
		})();
	}, []);

	useEffect(() => {
		setError(verifyAccessory(accessoryData));
		setApiError(null);
	}, [accessoryData]);

	const accessoryMaterials = accessoryData.materials;

	accessoryMaterials.sort((mat1, mat2) =>
		mat2.category.localeCompare(mat1.category)
	);

	const editAccessory = async () => {
		if (error) {
			return;
		}

		setIsSaving(true);

		const data = {
			...accessoryData,
			materials: accessoryData.materials.map((material) => ({
				...material,
				...(material.category == 'Labor'
					? { name: `${accessoryData.name} Labor` }
					: {}),
			})),
		};

		const result = await api.accessory.updateAccessory(data);

		setIsSaving(false);

		if (result.errored) {
			setApiError(result.error.message);
		} else {
			toastSuccess({ description: 'Accessory updated successfully' });
			onRefresh();
			onClose();
		}
	};

	return (
		<SidePanelDialog open={isOpen} onClose={onClose}>
			<DialogTitle
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					padding: '0px',
				}}
			>
				<Stack>
					<Typography fontWeight="bold">{accessory.name}</Typography>
					<Typography fontSize="13px" color="gray.500">
						Edit your pre-configured accessory type
					</Typography>
				</Stack>

				<IconButton onClick={onClose}>
					<Close />
				</IconButton>
			</DialogTitle>

			<Divider sx={{ marginTop: '10px' }} />

			{apiError && (
				<Typography
					sx={{
						color: 'error.main',
						fontSize: '13px',
						marginTop: '10px',
					}}
				>
					{apiError?.charAt(0).toUpperCase() + apiError?.slice(1)}
				</Typography>
			)}

			<DialogContent
				sx={{
					padding: '0px',
				}}
			>
				<AccessoryDataCard
					accessoryData={accessoryData}
					setAccessoryData={setAccessoryData}
					calculationTypes={calculationTypes}
				/>

				<Box
					sx={{
						marginTop: '20px',
						borderRadius: '10px',
						padding: '10px',
						border: '1px solid #E5E7EB',
						paddingBottom: '20px',
					}}
				>
					<Stack
						display="grid"
						gridTemplateColumns="45% 27% 27%"
						alignItems="center"
					>
						<Typography fontWeight="bold">
							Included Materials & Labor
						</Typography>

						<Button
							color="primary"
							startIcon={<AddIcon />}
							sx={{}}
							onClick={() =>
								setAccessoryData((prev) => ({
									...prev,
									materials: [...prev.materials, defaultMaterial],
								}))
							}
						>
							Add Material
						</Button>

						<Button
							color="primary"
							startIcon={<AddIcon />}
							onClick={() =>
								setAccessoryData((prev) => ({
									...prev,
									materials: [...prev.materials, defaultLabor],
								}))
							}
						>
							Add Labor
						</Button>
					</Stack>

					{accessoryData.materials.map((accessoryMaterial, index) => (
						<AccessoryMaterialDataCard
							key={index}
							index={index}
							accessoryMaterialData={accessoryMaterial}
							setAccessoryData={setAccessoryData}
							accessoryData={accessoryData}
						/>
					))}
				</Box>
			</DialogContent>

			<DialogActions>
				<Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: '25px' }}>
					<Button sx={{ color: 'black' }} onClick={onClose}>
						Cancel
					</Button>

					<Button
						sx={{
							backgroundColor: 'primary.main',
							color: 'white',
						}}
						onClick={editAccessory}
						disabled={Boolean(error) || isSaving}
					>
						{isSaving ? (
							<Spinner size={SpinnerSize.SMALL} />
						) : (
							'Update Accessory'
						)}
					</Button>
				</Stack>
			</DialogActions>
		</SidePanelDialog>
	);
};

export default EditAccessory;
